import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";

const DMY_FORMAT = "DD-MM-YYYY";
const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Generates and downloads a PDF report from HTML content
 * @param {Object} options - Configuration options for PDF generation
 * @param {HTMLElement} options.contentRef - React ref to the content to be printed
 * @param {Object} options.restaurantData - Restaurant information
 * @param {boolean} options.isXReport - Whether this is an X-Report (true) or Z-Report (false)
 * @param {string} options.fromDate - Start date for the report
 * @param {string} options.toDate - End date for the report
 * @returns {Promise<void>}
 */
export const generateReportPDF = async ({
    contentRef,
    restaurantData,
    isXReport = false,
    fromDate,
    toDate
}) => {
    if (!contentRef || !contentRef.current) {
        console.error('Content reference is required for PDF generation');
        alert('Unable to generate PDF. Content not found.');
        return;
    }

    try {
        // Create a temporary container with the header and content for printing
        const printContainer = document.createElement('div');
        printContainer.style.cssText = `
            padding: 20px;
            background-color: white;
            color: black;
            font-family: Arial, sans-serif;
            position: absolute;
            top: -9999px;
            left: -9999px;
            width: 794px;
            z-index: -1;
        `;
        
        // Add header
        const header = document.createElement('div');
        header.innerHTML = `
            <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
                <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: #333;">
                    ${restaurantData?.name || 'Restaurant'}
                </h1>
                <h2 style="margin: 10px 0; font-size: 22px; color: #555;">
                    ${isXReport ? 'X' : 'Z'} Report
                </h2>
                <p style="margin: 10px 0; font-size: 16px; color: #666;">
                    Report Period: ${moment(fromDate, DATE_FORMAT).format(DMY_FORMAT)} - ${moment(toDate, DATE_FORMAT).format(DMY_FORMAT)}
                </p>
                <p style="margin: 5px 0; font-size: 14px; color: #888;">
                    Generated on: ${moment().format('DD-MM-YYYY HH:mm')}
                </p>
            </div>
        `;
        
        // Clone the content and apply print-friendly styles
        const content = contentRef.current.cloneNode(true);
        
        // Apply print-friendly styles to the content
        const style = document.createElement('style');
        style.textContent = `
            * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            .MuiCard-root {
                box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                border: 1px solid #e0e0e0 !important;
            }
            .MuiTypography-root {
                color: #333 !important;
            }
            .MuiDivider-root {
                border-color: #e0e0e0 !important;
            }
        `;
        
        // Append elements to container
        printContainer.appendChild(style);
        printContainer.appendChild(header);
        printContainer.appendChild(content);
        
        // Temporarily add to document
        document.body.appendChild(printContainer);
        
        // Generate canvas with high quality settings
        const canvas = await html2canvas(printContainer, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            allowTaint: false,
            foreignObjectRendering: true,
            imageTimeout: 15000,
            removeContainer: false
        });
        
        // Remove temporary container
        document.body.removeChild(printContainer);
        
        // Generate PDF
        const imgData = canvas.toDataURL('image/png', 1.0);
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm  
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        
        let position = 0;
        
        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        // Add additional pages if needed
        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }
        
        // Generate filename with date and report type
        const dateStr = moment().format('YYYY-MM-DD');
        const reportType = isXReport ? 'X-Report' : 'Z-Report';
        const restaurantName = restaurantData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Restaurant';
        const filename = `${reportType}_${restaurantName}_${dateStr}.pdf`;
        
        // Save the PDF
        pdf.save(filename);
        
        return {
            success: true,
            filename
        };
        
    } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Print report using browser's print functionality (alternative to PDF)
 * @param {Object} options - Configuration options for printing
 * @param {HTMLElement} options.contentRef - React ref to the content to be printed
 * @param {Object} options.restaurantData - Restaurant information
 * @param {boolean} options.isXReport - Whether this is an X-Report (true) or Z-Report (false)
 * @param {string} options.fromDate - Start date for the report
 * @param {string} options.toDate - End date for the report
 */
export const printReport = ({
    contentRef,
    restaurantData,
    isXReport = false,
    fromDate,
    toDate
}) => {
    if (!contentRef || !contentRef.current) {
        console.error('Content reference is required for printing');
        alert('Unable to print. Content not found.');
        return;
    }

    try {
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        // Get the content HTML
        const content = contentRef.current.innerHTML;
        
        // Create the print document
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${isXReport ? 'X' : 'Z'} Report - ${restaurantData?.name || 'Restaurant'}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 20px;
                        color: #333;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                        border-bottom: 2px solid #333;
                        padding-bottom: 20px;
                    }
                    .header h1 {
                        margin: 0;
                        font-size: 28px;
                        font-weight: bold;
                    }
                    .header h2 {
                        margin: 10px 0;
                        font-size: 22px;
                        color: #555;
                    }
                    .header p {
                        margin: 5px 0;
                        color: #666;
                    }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>${restaurantData?.name || 'Restaurant'}</h1>
                    <h2>${isXReport ? 'X' : 'Z'} Report</h2>
                    <p>Report Period: ${moment(fromDate, DATE_FORMAT).format(DMY_FORMAT)} - ${moment(toDate, DATE_FORMAT).format(DMY_FORMAT)}</p>
                    <p>Generated on: ${moment().format('DD-MM-YYYY HH:mm')}</p>
                </div>
                <div class="content">
                    ${content}
                </div>
            </body>
            </html>
        `;
        
        // Write content to print window
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Wait for content to load then print
        printWindow.onload = () => {
            printWindow.print();
            printWindow.close();
        };
        
    } catch (error) {
        console.error('Error printing report:', error);
        alert('Error printing report. Please try again.');
    }
};

const printUtils = {
    generateReportPDF,
    printReport
};

export default printUtils;
